/**
 * Shared utilities for classifying auth-related endpoints.
 * Used by AuthInterceptor and ErrorInterceptor for consistent handling.
 */

/**
 * Auth flow endpoints (login, 2FA, register, refresh, logout, activate, password reset).
 *
 * These carry their own credentials — a password, a one-time code, an activation or reset token —
 * so they are never wrapped in token-refresh recovery, and they must be sendable *while no session
 * exists*: the 2FA verify call happens after the password step and before any session, so if it
 * were treated as a protected call the "session is over" gate would refuse it and a user whose
 * session had just ended could never complete a 2FA login without reloading the page.
 * Logout should work even with expired tokens. Activate doesn't need marina headers since the user
 * isn't logged in yet.
 */
export function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/verify-2fa') ||
    url.includes('/auth/request-2fa-code') ||
    url.includes('/auth/register') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/activate') ||
    url.includes('/auth/send-activation-link') ||
    url.includes('/auth/reset-password')
  );
}

export function isRefreshEndpoint(url: string): boolean {
  return url.includes('/auth/refresh');
}

/**
 * Public resource endpoints that don't require authentication.
 */
export function isPublicResourceEndpoint(url: string): boolean {
  return url.includes('/resources/path/') || url.includes('/publicforms/');
}

/**
 * Endpoints where 401 should not trigger redirect to login.
 * Includes auth flow endpoints and public resources.
 */
export function shouldSkip401Redirect(url: string): boolean {
  return isAuthEndpoint(url) || isPublicResourceEndpoint(url);
}

const PUBLIC_AUTH_PATH_PREFIXES = [
  '/auth/activate',
  '/auth/setup-password',
  '/auth/reset-password',
] as const;

/**
 * True for invitation / password-setup / reset flows that must never be forced to Login.
 * Check both the optional URL and `window.location.pathname` — during bootstrap `Router.url`
 * is often `/` (truthy) while the browser is already on `/auth/setup-password?...`.
 * Preferring only the Router value previously sent Create Password deep links to Login.
 */
export function isPublicAuthFlowUrl(urlOrPath?: string | null): boolean {
  const candidates = new Set(
    [normalizeAppPath(urlOrPath), readBrowserPathname()].filter((path): path is string => !!path)
  );
  if (candidates.size === 0) {
    return false;
  }
  return [...candidates].some((path) =>
    PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  );
}

function normalizeAppPath(urlOrPath?: string | null): string {
  if (!urlOrPath) {
    return '';
  }
  const raw = String(urlOrPath).trim();
  if (!raw) {
    return '';
  }
  // Router urls may be "/auth/setup-password?token=..." or full absolute URLs.
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname || '';
    }
  } catch {
    // fall through
  }
  const noHash = raw.split('#')[0] ?? raw;
  const pathOnly = noHash.split('?')[0] ?? noHash;
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
}

function readBrowserPathname(): string {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  return window.location.pathname || '';
}
